import React, { useCallback, useEffect, useState } from "react";
import { pointsParams } from "../../../types/markup";
import {
  clone,
  getPathologyColor,
  getSpineZoneColor,
  getSpineSideColor,
} from "../utils";
import { useRecoilState } from "recoil";
import {
  controlState,
  markupState,
  positionState,
  zoomState,
  stepState,
  markupStepsState,
  contrastState,
  brightnessState,
  regionState,
  markupPathologyState,
  imageNameState,
  imageDataState,
} from "../markupAtoms";
import { dragBoundFunc, isPointInsidePolygon, lineLength } from "./utilits";
import { ScMarkupSpace } from "./styled";
import Draggable from "react-draggable";
import Konva from "konva";
import {
  Circle,
  Group,
  Layer,
  Line,
  Stage,
  Text,
  Label,
  Tag,
} from "react-konva";
import { useDebouncedAddStep } from "../hooks/useDebouncedAddStep";
import useImage from "use-image";
import Image from "./Image";
import { pathologyList } from "../../../constants";
import regionConfig from "../../../pages/Dashboard/Validate/regionConfig";
import sides from "../MarkupPanel/components/sides";

const DraggableElement: any = Draggable;

interface MarkupSpaceProps {
  frameHeight: number;
  frameWidth: number;
  imageSrc: string;
  pixelSpacing: number;
}

export const MarkupSpace: React.FC<MarkupSpaceProps> = ({
  frameHeight = 0,
  frameWidth = 0,
  imageSrc,
  pixelSpacing,
}) => {
  const [markup, setMarkup] = useRecoilState(markupState);
  const [control] = useRecoilState(controlState);
  const [zoom] = useRecoilState(zoomState);
  const [contrast] = useRecoilState(contrastState);
  const [brightness] = useRecoilState(brightnessState);
  const [step] = useRecoilState(stepState);
  const [position, setPosition] = useRecoilState(positionState);
  const [markupPathology, setMarkupPathology] =
    useRecoilState(markupPathologyState);
  const [markupSteps] = useRecoilState(markupStepsState);
  const [imageName] = useRecoilState(imageNameState);
  const [region] = useRecoilState(regionState);
  const [imageData] = useRecoilState(imageDataState);
  const [points, setPoints] = useState<pointsParams[]>([]);
  const [ruler, setRuler] = useState<pointsParams[] | undefined>(undefined);
  let [image] = useImage(imageSrc, "use-credentials");
  const addStep = useDebouncedAddStep();

  useEffect(() => {
    setPoints([]);
    if (markup) {
      setMarkupPathology((markup) => ({
        ...markup,
        [imageName]: markup[imageName].map((item) => {
          const { active, ...newItem } = item;
          return newItem;
        }),
      }));
    } else {
      setMarkupPathology((markup) => ({
        ...markup,
        [imageName]: markup[imageName].filter((item) => {
          return !(item.markupType === "segment" && item.points.length === 1);
        }),
      }));
    }
  }, [markup, imageName]);

  useEffect(() => {
    setRuler(undefined);
    setMarkup("");
  }, [image]);

  useEffect(() => {
    if (step > 1 && markupSteps[step - 1]) {
      setMarkupPathology((markup) => ({
        ...markup,
        [imageName]: markupSteps[step - 1].data,
      }));
      const lastStep = markupSteps[step - 1];
      const activeMarkup = lastStep.data[lastStep.data.length - 1];
      if (activeMarkup && ["drawLine", "movePoint"].includes(lastStep.step)) {
        setPoints(activeMarkup.points);
      }
    }
  }, [step]);

  const drawLine = useCallback(
    (e: any) => {
      if (!markup || control === "drag") {
        return null;
      }

      if (markup === "line" && (!ruler || ruler?.length === 2)) {
        setRuler([
          {
            x: e.evt.offsetX / imageData.coefficient,
            y: e.evt.offsetY / imageData.coefficient,
          },
        ]);
        return;
      } else if (markup === "line" && Array.isArray(ruler)) {
        setRuler([
          ...ruler,
          {
            x: e.evt.offsetX / imageData.coefficient,
            y: e.evt.offsetY / imageData.coefficient,
          },
        ]);
        return;
      }

      if (
        markupPathology[imageName].length &&
        markupPathology[imageName][markupPathology[imageName].length - 1]
          .zone === "intervertebralDisc" &&
        markupPathology[imageName].some((item) =>
          isPointInsidePolygon(
            {
              x: e.evt.offsetX / imageData.coefficient,
              y: e.evt.offsetY / imageData.coefficient,
            },
            item.points
          )
        )
      ) {
        return;
      }

      if (points.length === 0) {
        createObject(e);

        setPoints((points) => [
          ...points,
          {
            x: e.evt.offsetX / imageData.coefficient,
            y: e.evt.offsetY / imageData.coefficient,
          },
        ]);
      } else {
        const updateObject = clone([...markupPathology[imageName]]);
        updateObject[markupPathology[imageName].length - 1].points = [
          ...points,
          {
            x: e.evt.offsetX / imageData.coefficient,
            y: e.evt.offsetY / imageData.coefficient,
          },
        ];
        addStep({
          step: "drawLine",
          data: updateObject,
        });

        setPoints((points) => [
          ...points,
          {
            x: e.evt.offsetX / imageData.coefficient,
            y: e.evt.offsetY / imageData.coefficient,
          },
        ]);

        if (markup === "segment") {
          setMarkup("");
        }
      }
    },
    [markup, markupPathology, points, control, ruler]
  );

  const createObject = (e: any) => {
    if (markup === "segment") {
      setMarkupPathology((markup) => ({
        ...markup,
        [imageName]: clone([
          ...markup[imageName],
          {
            ...(region === "spine" ? { spineSide: "Anterior" } : {}),
            color: "#FF0000",
            active: true,
            markupType: "segment",
            points: [
              {
                x: e.evt.offsetX / imageData.coefficient,
                y: e.evt.offsetY / imageData.coefficient,
              },
            ],
          },
        ]),
      }));
    } else {
      setMarkupPathology((markup) => ({
        ...markup,
        [imageName]: clone([
          ...markup[imageName],
          {
            ...(regionConfig[region].multiplePathologies
              ? { name: [pathologyList[region][0].id] }
              : { name: pathologyList[region][0].id }),
            color: "#FF0000",
            active: true,
            side: sides[region][0].value,
            ...(!regionConfig[region].noDegree ? { degree: 2 } : {}),
            markupType: "polygon",
            points: [
              {
                x: e.evt.offsetX / imageData.coefficient,
                y: e.evt.offsetY / imageData.coefficient,
              },
            ],
          },
        ]),
      }));
    }
  };

  const movePoint = (pos: any, pKey: number, objectKey: number | string) => {
    const boundary = dragBoundFunc(pos, frameHeight, frameWidth);

    if (objectKey === "ruler") {
      setRuler((ruler) => {
        if (!ruler) return;
        const newRuler = clone([...ruler]);
        newRuler[pKey].x = boundary.x / imageData.coefficient;
        newRuler[pKey].y = boundary.y / imageData.coefficient;
        return newRuler;
      });

      return boundary;
    }

    const newMarkupData = clone([...markupPathology[imageName]]);
    newMarkupData[objectKey].active = true;
    newMarkupData[objectKey].points[pKey].x =
      boundary.x / imageData.coefficient;
    newMarkupData[objectKey].points[pKey].y =
      boundary.y / imageData.coefficient;
    setMarkupPathology((markup) => ({
      ...markup,
      [imageName]: newMarkupData,
    }));
    addStep({
      step: "movePoint",
      data: newMarkupData,
    });
    return boundary;
  };

  const endMovePoint = (objectKey: number | string) => {
    if (objectKey === "ruler") {
      return;
    }
    const newMarkupData = clone([...markupPathology[imageName]]);
    newMarkupData[objectKey].active = false;
    setMarkupPathology((markup) => ({
      ...markup,
      [imageName]: newMarkupData,
    }));
  };

  const trackPos = (e: any) => {
    setPosition({
      x: e.x,
      y: e.y,
    });
  };

  const setActiveItem = useCallback((objectKey) => {
    setMarkupPathology((markup) => ({
      ...markup,
      [imageName]: markup[imageName]?.map((item, key) => {
        if (key === objectKey) {
          if (!item.active) {
            return {
              ...item,
              active: true,
            };
          } else {
            return {
              ...item,
              active: false,
            };
          }
        }
        if ("active" in item) {
          return {
            ...item,
            active: false,
          };
        }
        return item;
      }),
    }));
  }, []);

  const dragPoint = useCallback(
    (e, data) => {
      return control === "drag" && trackPos(data);
    },
    [control, trackPos]
  );

  return (
    <ScMarkupSpace className={control === "drag" ? "drag" : "select"}>
      <DraggableElement position={position} onDrag={dragPoint}>
        <div
          className="box"
          style={{
            height: frameHeight + "px",
          }}
        >
          <Stage
            width={frameWidth}
            height={frameHeight}
            style={{
              transform: `scale(${zoom}%)`,
            }}
            onClick={drawLine}
          >
            <Layer>
              <Group>
                <Image
                  image={image}
                  width={frameWidth}
                  height={frameHeight}
                  filters={[Konva.Filters.Contrast, Konva.Filters.Brighten]}
                  contrast={contrast}
                  brightness={brightness}
                />
                {markupPathology[imageName]?.map(
                  (
                    { points, markupType, name, active, zone, spineSide },
                    key
                  ) => (
                    <React.Fragment key={"line-" + key}>
                      <Group
                        onClick={() => {
                          if (control === "select") {
                            setActiveItem(key);
                          }
                        }}
                      >
                        <Line
                          strokeScaleEnabled={false}
                          draggable={false}
                          strokeWidth={Math.abs(2 / (zoom / 100))}
                          strokeEnabled={true}
                          closed
                          points={points.flatMap(({ x, y }) => [
                            x * imageData.coefficient,
                            y * imageData.coefficient,
                          ])}
                          stroke={
                            markupType === "polygon" || points.length > 2
                              ? ["spine"].includes(region)
                                ? getSpineZoneColor(zone)
                                : getPathologyColor(name, region)
                              : getSpineSideColor(spineSide)
                          }
                          fill={`rgba(${
                            markupType === "polygon" || points.length > 2
                              ? ["spine"].includes(region)
                                ? getSpineZoneColor(zone, true)
                                : getPathologyColor(name, region, true)
                              : getSpineSideColor(spineSide)
                          }, 0.2)`}
                        />
                        {points.map(({ x, y }, pKey) => (
                          <Circle
                            dragBoundFunc={(pos: any) =>
                              movePoint(pos, pKey, key)
                            }
                            onDragEnd={() => endMovePoint(key)}
                            draggable={control === "select"}
                            key={"circle" + pKey}
                            x={x * imageData.coefficient}
                            y={y * imageData.coefficient}
                            radius={Math.abs(2 / (zoom / 100))}
                            stroke={`#000000`}
                            strokeWidth={Math.abs(1 / (zoom / 100))}
                            fill={`rgba(82, 82, 82, 1)`}
                          />
                        ))}
                      </Group>
                    </React.Fragment>
                  )
                )}
                {ruler && (
                  <Group>
                    <Line
                      strokeScaleEnabled={false}
                      draggable={false}
                      strokeWidth={2}
                      strokeEnabled={true}
                      points={ruler.flatMap(({ x, y }) => [
                        x * imageData.coefficient,
                        y * imageData.coefficient,
                      ])}
                      stroke={"red"}
                      fill={`red`}
                    />
                    {ruler.map(({ x, y }, pKey) => (
                      <Circle
                        dragBoundFunc={(pos: any) =>
                          movePoint(pos, pKey, "ruler")
                        }
                        onDragEnd={() => endMovePoint("ruler")}
                        draggable={control === "select"}
                        key={"circle" + pKey}
                        x={x * imageData.coefficient}
                        y={y * imageData.coefficient}
                        radius={2}
                        stroke={`#000000`}
                        strokeWidth={1}
                        fill={`rgba(82, 82, 82, 1)`}
                      />
                    ))}
                    {ruler.length === 2 && (
                      <Label
                        x={ruler[1].x * imageData.coefficient + 4}
                        y={ruler[1].y * imageData.coefficient}
                      >
                        <Tag fill="blue" cornerRadius={8} />
                        <Text
                          text={
                            Math.abs(
                              lineLength(ruler[0], ruler[1]) * pixelSpacing
                            ).toFixed(2) + " мм"
                          }
                          fontSize={10}
                          fill="white"
                          padding={4}
                        />
                      </Label>
                    )}
                  </Group>
                )}
              </Group>
            </Layer>
          </Stage>
        </div>
      </DraggableElement>
    </ScMarkupSpace>
  );
};
